import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Music2 } from "lucide-react";
import { z } from "zod";

const searchSchema = z.object({
  genre_id: z.string().optional(),
  business_type_id: z.string().optional(),
});

export const Route = createFileRoute("/_authed/albums")({
  validateSearch: searchSchema,
  head: () => ({ meta: [{ title: "Albums — Sura" }] }),
  component: AlbumsPage,
});

type Album = { id: string; title: string; description?: string; track_count?: number };

function colorFor(id: string) {
  const hues = [220, 250, 200, 180, 270, 230];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return hues[h % hues.length];
}

function AlbumsPage() {
  const search = Route.useSearch();
  const [albums, setAlbums] = useState<Album[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/api/albums", { params: { page: 1, limit: 30, ...search } }).then(({ data }) => {
      setAlbums(data.albums || data.items || data || []);
    }).finally(() => setLoading(false));
  }, [search.genre_id, search.business_type_id]);

  return (
    <div className="px-4 md:px-8 py-6 md:py-10 max-w-7xl mx-auto">
      <h1 className="text-2xl md:text-3xl font-bold tracking-tight mb-6">Albums</h1>

      {loading ? (
        <div className="text-center text-muted-foreground py-12">Loading…</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {albums.map((a) => {
            const hue = colorFor(a.id);
            return (
              <Link key={a.id} to="/album/$id" params={{ id: a.id }} className="group">
                <div
                  className="aspect-square rounded-2xl mb-3 flex items-center justify-center text-primary-foreground shadow-[var(--shadow-card)] group-hover:shadow-[var(--shadow-soft)] transition-shadow"
                  style={{ background: `linear-gradient(135deg, oklch(0.65 0.16 ${hue}), oklch(0.5 0.18 ${hue + 30}))` }}
                >
                  <Music2 className="h-10 w-10 opacity-80" />
                </div>
                <div className="font-medium truncate">{a.title}</div>
                {a.description && <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{a.description}</div>}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
